// The comment-box placement MATH (below vs. flip-above vs. clamp) is extracted
// into the pure helper below and unit-tested there; jsdom does no layout, so
// the on-screen flip itself is verified in the browser / epic env.
// covered-by: frontends/carrie/src/routes/tickets/components/CommentTool/__tests__/commentBoxPosition.test.ts
// The on-canvas scroll/pin behavior this box delegates to <Comment isCanvas/>
// (scrolling body + pinned reply footer + tldraw wheel-stop) is asserted in the
// Comment component test; the actual over-canvas wheel routing is browser/epic
// verified since jsdom can't drive tldraw's gesture layer.
// covered-by: frontends/carrie/src/routes/tickets/routes/components/comment-drawer/__tests__/Comment.test.tsx
// The Escape-to-close decision (close the bubble unless the @-mentions dropdown
// owns the keypress) is a pure function unit-tested in commentKeyboard.test.ts;
// the document-level listener wiring itself is browser/epic verified.
// covered-by: frontends/carrie/src/routes/tickets/components/CommentTool/__tests__/commentKeyboard.test.ts
import {
  baseTicketCommentSchema,
  type BaseTicketComment,
  type CreateTicketCommentRequest,
} from "@mg/schemas/src/christo/catalyst";
import {
  EnterpriseProfileType,
  TicketCommentBoard,
  TicketCommentDisposition,
} from "@mg/schemas/src/commons";
import { X } from "@phosphor-icons/react";
import {
  Checkbox,
  Flex,
  Grid,
  IconButton,
  Select,
  Text,
} from "@radix-ui/themes";
import cx from "classnames";
import { useEffect, useRef, useState, useMemo } from "react";
import {
  HTMLContainer,
  Rectangle2d,
  ShapeUtil,
  type RecordProps,
  type TLBaseShape,
  T,
  stopEventPropagation,
  type TLShapeId,
} from "tldraw";

import {
  getCommentBoxPinCorner,
  getCommentBoxPlacement,
  getCommentBoxTopY,
} from "./commentBoxPosition";
import {
  isMentionsDropdownOpen,
  shouldCloseCommentOnEscape,
} from "./commentKeyboard";

import { CommentLoginWall } from "../../../../components/CommentLoginWall";
import { useUI } from "../../../../contexts/ui";
import {
  errorAnalyticsPayload,
  useAnalytics,
} from "../../../../utils/analytics";
import { isPunttGuest } from "../../../../utils/auth";
import { isNil } from "../../../../utils/fp";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { errorMessage } from "../../../../utils/http";
import {
  useCreateAskPunttComment,
  useNewCommentMutation,
} from "../../../../utils/queries/projects";
import { getAiRuleMap } from "../../../../utils/selections";
import {
  setActiveCommentId,
  setComments,
  setVisibleCommentShapes,
} from "../../../../utils/slices/ticket";
import {
  isCommentShapeVisible,
  useGetVisibleUsers,
} from "../../../../utils/tldraw/comments";
import { Comment } from "../../routes/components/comment-drawer/Comment";
import {
  CommentMentions,
  type Selections,
} from "../CommentMentions/CommentMentions";

export type CommentShape = TLBaseShape<
  "comment",
  {
    w: number;
    h: number;
    commentId: unknown;
    isLocked?: boolean;
  }
>;

export class CommentShapeUtil extends ShapeUtil<CommentShape> {
  static override type = "comment" as const;

  static override props: RecordProps<CommentShape> = {
    w: T.number,
    h: T.number,
    commentId: T.unknown,
    isLocked: T.boolean.optional(),
  };

  override isAspectRatioLocked = (_shape: CommentShape) => false;
  override canResize = (_shape: CommentShape) => false;
  override hideRotateHandle = (_shape: CommentShape) => true;
  override canBind = () => true;
  override canEdit = () => true;

  getDefaultProps(): CommentShape["props"] {
    return {
      w: 0,
      h: 0,
      commentId: null,
      isLocked: false,
    };
  }

  private isVisible(shape: CommentShape, userId?: string) {
    return isCommentShapeVisible(shape, userId);
  }

  getGeometry(shape: CommentShape) {
    const zoomLevel = this.editor.getZoomLevel();

    return new Rectangle2d({
      width: shape.props.w / zoomLevel,
      height: shape.props.h / zoomLevel,
      isFilled: true,
    });
  }

  component = (shape: CommentShape) => {
    const zoomLevel = this.editor.getZoomLevel();
    const selectedShapeIds = this.editor.getSelectedShapeIds();
    const users = useGetVisibleUsers();
    const aiRuleMap = getAiRuleMap();
    const { notify } = useUI();

    // Slices
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.value);
    const ticket = useAppSelector((state) => state.ticket.value);
    const versions = useAppSelector((state) => state.ticket.revisions);
    const reversedVersionIndex = useAppSelector(
      (state) => state.ticket.reversedVersionIndex,
    );
    const selectedVersion = isNil(reversedVersionIndex)
      ? undefined
      : versions[reversedVersionIndex];
    const comments = useAppSelector((state) => state.ticket.comments);
    const visibleCommentShapes = useAppSelector(
      (state) => state.ticket.visibleCommentShapes,
    );

    // Queries and mutations
    const createCommentMutation = useNewCommentMutation();
    const askPunttMutation = useCreateAskPunttComment();

    // Analytics
    const posthog = useAnalytics("CommentTool");

    // Local state
    const mentionsRef = useRef<{
      getSelections: () => Selections;
      closeMentions: () => void;
    }>();
    const containerRef = useRef<HTMLDivElement>(null);
    const [required, setRequired] = useState<boolean | "indeterminate">(true);
    const [rule, setRule] = useState("no_rule");
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<string>(
      (shape.meta?.tempComment as string | null) ?? "",
    );
    const isSelected =
      selectedShapeIds.includes(shape.id) ||
      selectedShapeIds.includes(shape.meta?.linkedAvatarId as string);
    // Share-link / unauthenticated viewers get length-preserving lorem from
    // the API, so any bubble we open for them would render readable (but
    // fake) text. The sidebar already swaps these threads for the blurred
    // <CommentLoginWall>; do the same inside the canvas bubble so guests
    // see a consistent lockout wherever comments appear.
    const isGuest = user == null || isPunttGuest();
    const boardId = selectedVersion?._id;
    const userIsReviewing = comments.some(
      (c) => c.isPending && c.createdBy === user?.userID,
    );
    const comment = useMemo(() => {
      if (isNil(shape.meta) || isNil(shape.meta.comment)) return null;

      try {
        const commentObj = JSON.parse(shape.meta.comment as string);
        const comment = baseTicketCommentSchema.parse(commentObj);

        return comments.find((c) => c._id === comment._id);
      } catch {
        return null;
      }
    }, [comments, shape.meta?.comment]);

    // Add a key to force Comment component to remount when message changes
    const [commentReplyKey, setCommentReplyKey] = useState(0);

    // Which corner of the box sits against the pin tip, so we can leave that
    // one square ("point to point" with the pin) while rounding the rest. It
    // tracks both the vertical flip (placement above/below the pin) and the
    // horizontal shift (whether the box extends right from the pin or is
    // nudged left to stay on screen). Defaults to top-left: the box opens
    // below-and-to-the-right of the pin in the common case.
    const [pinCorner, setPinCorner] = useState<"tl" | "tr" | "bl" | "br">("tl");

    // Effect chain

    useEffect(() => {
      const newSize = isSelected
        ? {
            w: 300,
            h: 150,
          }
        : { w: 0, h: 0 };
      // shape wont change inside of render, so must define the changes here to user it with parsed origin point
      const shapeWithSize = { ...shape, props: newSize };

      if (shape.props.w !== newSize.w) {
        this.editor.updateShape(shapeWithSize);
      }

      if (isSelected) {
        this.editor.bringToFront([
          shape.id,
          shape.meta.linkedAvatarId as TLShapeId,
        ]);
        if (!isNil(comment?._id)) {
          dispatch(setActiveCommentId(comment._id));
        }
      } else {
        if (isNil(comment)) {
          this.editor.deleteShapes([
            shape.id,
            shape.meta.linkedAvatarId as TLShapeId,
          ]);
        } else {
          dispatch(setActiveCommentId(null));
        }
      }
    }, [comment, isSelected]);

    useEffect(() => {
      if (mentionsRef.current) {
        mentionsRef.current?.closeMentions();
      }
    }, [zoomLevel]);

    // Escape closes the open comment bubble, mirroring the X button. We listen
    // on the document so it works regardless of whether focus is in the reply
    // textarea, the canvas, or nowhere. When the @-mentions dropdown is open it
    // owns Escape (to close itself first), so we leave the bubble open in that
    // case — a second Escape then closes the bubble.
    useEffect(() => {
      if (!isSelected) return;
      const onKeyDown = (e: KeyboardEvent) => {
        if (!shouldCloseCommentOnEscape(e.key, isMentionsDropdownOpen())) {
          return;
        }
        this.editor.selectNone();
        dispatch(setActiveCommentId(null));
      };
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    }, [isSelected]);

    useEffect(() => {
      const pinId = shape.meta.linkedAvatarId as TLShapeId;
      const pinShape = this.editor.getShape(pinId);
      if (pinShape) {
        this.editor.updateShape({
          ...pinShape,
          meta: { ...pinShape.meta, isRequired: !!required },
        });
      }
    }, [required]);

    useEffect(() => {
      const parentShape = this.editor.getShape(
        shape.meta.linkedAvatarId as TLShapeId,
      );
      if (!parentShape) return;

      const pinBottomY = getCommentBoxTopY(parentShape.y, zoomLevel);

      if (!isSelected) {
        this.editor.updateShape({
          ...shape,
          props: {
            ...shape.props,
            w: 0,
            h: 0,
          },
          x: parentShape.x,
          y: pinBottomY,
        });
        return;
      }

      // Detects if the comment box will open near the edge of the canvas and,
      // if so, repositions it so it stays fully on screen: shifts left when it
      // would run off the right, and flips ABOVE the pin tip (clamping within
      // bounds) when it would overflow the bottom.
      const observer = new ResizeObserver(() => {
        if (containerRef.current) {
          const { height: shapeHeight } =
            containerRef.current.getBoundingClientRect();
          if (!parentShape) {
            return;
          }
          let x = parentShape.x;
          const viewport = this.editor.getViewportScreenBounds();

          // When the box would run off the right edge we nudge it left so it
          // extends to the LEFT of the pin instead of the right; track that so
          // the squared corner stays on whichever horizontal side the pin tip
          // actually touches.
          const pinOnRight = parentShape.x + 600 > viewport.width;
          if (pinOnRight) {
            x = parentShape.x - 300 / zoomLevel;
          }

          // Measure overflow against the visible PAGE bounds (the same space
          // the shape lives in) rather than the screen viewport, then let the
          // pure helper decide below-vs-above-vs-clamp. `getBoundingClientRect`
          // returns post-zoom screen pixels, so divide by `zoomLevel` to bring
          // the box height back into page space.
          const pageBounds = this.editor.getViewportPageBounds();
          const { top: y, placement } = getCommentBoxPlacement({
            pinY: parentShape.y,
            zoomLevel,
            boxHeight: shapeHeight / zoomLevel,
            minY: pageBounds.minY,
            maxY: pageBounds.maxY,
          });

          // The pin tip touches the box on its TOP edge when the box opens
          // below the pin, and on its BOTTOM edge when it flips above. Combine
          // that with the horizontal side to pick the single corner adjacent
          // to the pin; that corner stays square (see the render below).
          setPinCorner(getCommentBoxPinCorner(placement, pinOnRight));

          this.editor.updateShape({
            ...shape,
            x,
            y,
          });
        }
      });

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        if (containerRef.current) {
          observer.unobserve(containerRef.current);
        }
      };
    }, [isSelected, shape, zoomLevel]);

    this.editor.addListener("event", (e) => {
      if (e.type === "wheel" && mentionsRef.current) {
        mentionsRef.current?.closeMentions();
      }
    });
    const handleSetMessage = (comm: string) => {
      setMessage(comm);
      this.editor.updateShape({
        ...shape,
        meta: { ...shape.meta, tempComment: comm },
      });
    };

    function handleGetSelections() {
      if (mentionsRef.current) {
        return mentionsRef.current.getSelections();
      }
    }

    if (
      shape.props.w == 0 ||
      shape.props.h == 0 ||
      !isSelected ||
      !this.isVisible(shape, user?.userID)
    ) {
      return (
        <HTMLContainer
          id={shape.id}
          style={{
            width: 0,
            height: 0,
          }}
        />
      );
    }

    // Arrow functions have transient `this` context.
    const renderCommentContents = () => {
      if (!isNil(comment) && isGuest) {
        // Mirrors the sidebar treatment: don't render the lorem-redacted
        // body at all. CommentLoginWall is pointer-events-auto on its CTA
        // only, so the "Log in to see comments" link still works; clicks
        // elsewhere fall through to the bubble like normal.
        return (
          <div
            className="min-h-0 flex-1 overflow-y-auto"
            onPointerDown={stopEventPropagation}
            data-testid="canvas-comment-login-wall"
          >
            <CommentLoginWall count={1} section="comments" />
          </div>
        );
      }
      if (!isNil(comment)) {
        // The Comment owns its own scroll body + pinned reply footer when
        // rendered on the canvas (isCanvas), so this wrapper is just a flex
        // column that fills the available height — it must NOT scroll here, or
        // it would scroll the pinned reply out of view again.
        return (
          <div
            className="flex min-h-0 flex-1 flex-col"
            onPointerDown={stopEventPropagation}
          >
            <Comment
              mentions={comment.mentions as string[]}
              aiPersona={comment.aiPersona}
              key={`${comment._id}-${commentReplyKey}`}
              author={
                comment.createdBy as Exclude<typeof comment.createdBy, string>
              }
              createdAt={comment.createdAt}
              isAI={comment.isAI}
              message={comment.description}
              messageId={comment._id}
              commentId={comment._id}
              meta={comment}
              replies={comment.messages}
              updatedAt={comment.updatedAt}
              rule={comment.rule}
              onSuccess={(data, eventName) => {
                if (data != null) {
                  if (
                    user?.role !== EnterpriseProfileType.CATALYST_AI &&
                    user?.role !== EnterpriseProfileType.MEANINGFUL_GIGS &&
                    (data.disposition == TicketCommentDisposition.DISMISSED ||
                      data.disposition == TicketCommentDisposition.RESOLVED)
                  ) {
                    this.editor.deleteShapes([shape.id]);
                  }
                  const replaceCommentIndex = comments.findIndex(
                    (comment) => comment._id === data._id,
                  );
                  const commentsCopy = [...comments];
                  commentsCopy.splice(replaceCommentIndex, 1, data);

                  dispatch(setComments(commentsCopy));
                }
                this.editor.bringToFront([shape.id]);

                posthog.capture("changed_comment", {
                  isAI: data.isAI,
                  comment: data,
                  eventName,
                });

                // Clear message and force Comment component to remount
                handleSetMessage("");
                setCommentReplyKey((prev) => prev + 1);

                this.editor.updateShape({
                  ...shape,
                  meta: {
                    ...shape.meta,
                    comment: JSON.stringify(data),
                    tempComment: "",
                  },
                  props: {
                    ...shape.props,
                    commentId: data._id,
                  },
                });
              }}
              onError={(error: Error | Response, data, operation, payload) => {
                notify({
                  title: "Error",
                  message: `An error occured while ${operation}: ${errorMessage(error)}`,
                  timeout: 8000,
                  variant: "error",
                });
                posthog.capture("comment_reply_error", {
                  ...errorAnalyticsPayload(error),
                  operation,
                  threadId: comment._id,
                  numExistingReplies: comment.messages.length,
                  ...Object.fromEntries(
                    Object.entries(payload).map(([key, value]) => [
                      "payload_" + (Array.isArray(value) ? "num_" : "") + key,
                      Array.isArray(value) ? value.length : value,
                    ]),
                  ),
                });
              }}
              editor={this.editor}
              users={users}
              isCanvas
              setMessage={handleSetMessage}
              tempComment={message}
            />
          </div>
        );
      }

      if (isGuest) {
        // Guests shouldn't see a "compose a new comment" box either —
        // they can't post, and the empty input is just confusing.
        return (
          <div
            className="min-h-0 flex-1 overflow-y-auto"
            onPointerDown={stopEventPropagation}
            data-testid="canvas-comment-login-wall"
          >
            <CommentLoginWall count={1} section="comments" />
          </div>
        );
      }

      return (
        <Grid
          gap="4"
          p="4"
          className="min-h-0 flex-1 overflow-y-auto"
          onPointerDown={stopEventPropagation}
        >
          <CommentMentions
            // Keep this as "comment" so the derived send button stays
            // `comment-send-button`, which the canvas comment e2e specs target.
            data-testid="comment"
            value={message}
            ref={mentionsRef}
            onChange={handleSetMessage}
            placeholder="Comment or Ask @Puntt"
            disabled={
              createCommentMutation.isPending ||
              !message.trim().length ||
              isLoading
            }
            loading={createCommentMutation.isPending || isLoading}
            // TODO: let's break this logic out into some into a separate
            // function so that we can keep this logic concern deal only with
            // the shape and be agnostic to how we're adding shapes to the board
            // (even though we are literally dealing within the context of
            // TLDraw). For example, the API of TLDraw may change, but the way
            // you construct shapes may stay the same, etc.
            onSend={async () => {
              if (isLoading) return;
              // No selected revision means there is no board to attach the
              // comment to. Bail before showing a loading state — `boardId`
              // (`selectedVersion?._id`) is `string | undefined`, and this
              // guard narrows it to a definite `string` for both the new-comment
              // payload and the Ask Puntt mutation below.
              if (isNil(boardId)) return;
              setIsLoading(true);

              const selections = handleGetSelections();
              const parsedOriginPoint =
                shape.meta?.originalCoords &&
                (JSON.parse(shape.meta.originalCoords as string) as {
                  x?: number;
                  y?: number;
                });

              const payload: CreateTicketCommentRequest["body"] = {
                description: message,
                x: parsedOriginPoint ? parsedOriginPoint.x : shape.x,
                y: parsedOriginPoint
                  ? (parsedOriginPoint?.y ?? 0) - 26 / zoomLevel
                  : shape.y - 26 / zoomLevel, // minus 26px is needed for the offset since we're creating the comment pin shape differently
                boardId,
                boardType: TicketCommentBoard.REVISION,
                isPending: userIsReviewing,
                isRequired: required as boolean,
                rule: !required || rule === "no_rule" ? undefined : rule,
              };
              const sisterShape = this.editor.getShape(
                shape.meta.linkedAvatarId as TLShapeId,
              );
              const onSuccess = (data: BaseTicketComment) => {
                this.editor.bringToFront([shape.id]);

                this.editor.updateShapes([
                  {
                    ...shape,
                    meta: {
                      ...shape.meta,
                      comment: JSON.stringify(data),
                    },
                    props: {
                      ...shape.props,
                      commentId: data._id,
                    },
                  },
                  // @ts-expect-error TS2322: tlshape type isn't expecting meta here
                  {
                    ...sisterShape,
                    meta: {
                      ...sisterShape?.meta,
                      comment: JSON.stringify(data),
                    },
                  },
                ]);
                const commentWithShapeId = {
                  ...data,
                  shapeId: shape.id,
                };
                dispatch(
                  setVisibleCommentShapes([
                    ...visibleCommentShapes,
                    commentWithShapeId,
                  ]),
                );

                dispatch(setComments([...comments, data]));
              };

              if (selections?.find((s) => s._id === "@puntt")) {
                const tempCommentData = {
                  ...payload,
                  _id: (Math.random() * 10000).toString(),
                  createdBy: {
                    _id: user?.userID,
                    name: user?.name ?? "Anonymous",
                    avatar: user?.avatar,
                  },
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  isDeleted: false,
                  isReview: false,
                  votes: [],
                  isAI: false,
                  mentions: [],
                  messages: [
                    {
                      createdBy: {
                        _id: "63bc994226c323d41d67a11f",
                        name: "AI Reviewer",
                        avatar:
                          "users/615f5203e1cb445ef2486b74/3-color-logo-white-bg-square.png",
                      },
                      aiPersona: {
                        name: "",
                        avatar: "",
                      },
                      description: "",
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                      isDeleted: false,
                      isPending: false,
                      isReview: false,
                      votes: [],
                      isAI: true,
                      reviewId: "67082ad6ecce06a419fe876d",
                      mentions: [],
                      messages: [],
                      x: 20,
                      y: 20,
                      isRequired: false,
                      boardType: "revisionBoard",
                      boardId: "6706ca0b6a3ca36a292610a5",
                      disposition: "default",
                      _id: (Math.random() * 100000).toString(),
                      aiPending: true,
                    },
                  ],
                  isRequired: required as boolean,
                  disposition: "default" as TicketCommentDisposition,
                };
                onSuccess(tempCommentData);
                setIsLoading(false);
                handleSetMessage("");
                // this is where we will have the call to get the ai response and then we will update as below
                if (!ticket?._id) {
                  notify({
                    message:
                      "Ticket data not available — please reload the page.",
                    timeout: 8000,
                    variant: "error",
                  });
                  setIsLoading(false);
                  handleSetMessage("");
                  return;
                }
                return askPunttMutation
                  .mutateAsync({
                    ticketId: ticket._id,
                    description: message,
                    x: payload.x as number,
                    y: payload.y as number,
                    boardId,
                    shapeIds: [],
                    isRequired: required as boolean,
                  })
                  .then((res) => {
                    onSuccess(res);
                  })
                  .catch(() => {
                    dispatch(setComments(comments));
                    const deletingShapes = [shape.id];

                    if (sisterShape != null) {
                      deletingShapes.push(sisterShape.id);
                    }

                    this.editor.deleteShapes(deletingShapes);
                    notify({
                      message: "An error occurred while asking Puntt AI",
                      timeout: 8000,
                      variant: "error",
                    });
                  })
                  .finally(() => {
                    handleSetMessage("");
                    setIsLoading(false);
                  });
              }

              if (selections?.length) {
                payload.mentions = selections.map((s) => s._id as string);
              }

              handleSetMessage("");

              if (!ticket?._id) {
                notify({
                  message:
                    "Ticket data not available — please reload the page.",
                  timeout: 8000,
                  variant: "error",
                });
                return;
              }

              createCommentMutation.mutate(
                {
                  ticketId: ticket._id,
                  payload,
                },
                {
                  onSuccess: (data) => {
                    posthog.capture("added_new_comment", {
                      isAI: data.isAI,
                      comment: data,
                    });

                    onSuccess(data);
                  },

                  onSettled() {
                    setIsLoading(false);
                  },
                },
              );
            }}
          />

          <Text
            as="label"
            data-testid="required-change-label"
            className={cx("text-base-black", {
              hidden:
                user?.role === EnterpriseProfileType.CATALYST_REQUESTER ||
                user?.role === EnterpriseProfileType.CATALYST_CREATIVE,
            })}
          >
            <Flex gap="2" className="items-center">
              <Checkbox
                data-testid="comment-required-toggle"
                defaultChecked={
                  user?.role !== EnterpriseProfileType.CATALYST_REQUESTER &&
                  user?.role !== EnterpriseProfileType.CATALYST_CREATIVE
                }
                onCheckedChange={setRequired}
              />
              Required Change
            </Flex>
          </Text>
          {required &&
            (user?.role === EnterpriseProfileType.MEANINGFUL_GIGS ||
              user?.role === EnterpriseProfileType.CATALYST_AI) && (
              <Select.Root size="3" value={rule} onValueChange={setRule}>
                <Select.Trigger
                  className="w-full"
                  variant="surface"
                  color="gray"
                />
                <Select.Content>
                  <Select.Item value="no_rule">No Rule</Select.Item>
                  {Object.keys(aiRuleMap).map((key) => (
                    <Select.Item key={key} value={key}>
                      {aiRuleMap[key]}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            )}
        </Grid>
      );
    };

    return (
      <HTMLContainer
        // colon is not a valid selector character
        id={shape.id.replace(":", "-")}
        style={{
          transform: `scale(${1 / zoomLevel})`,
          width: shape.props.w,
          position: "relative",
        }}
        data-testid="comment-container"
      >
        <div
          role="button"
          tabIndex={0}
          className={cx(
            // `overflow-hidden` clips every child to the `rounded-2xl` radius.
            // Without it the inner full-bleed children that paint a white
            // background (the pinned reply footer, the comment grid) square off
            // the bottom corners and the rounding never shows. The squared pin
            // corner keeps a 0 radius via the per-corner `-none` below, so it
            // still meets the pin tip point-to-point.
            "relative -ml-0.5 flex max-h-[min(70vh,480px)] cursor-default flex-col overflow-hidden rounded-2xl border bg-base-white shadow-lg [pointer-events:all]",
            // Round every corner EXCEPT the one touching the pin tip, so the
            // box reads "point to point" with the pin. `pinCorner` tracks the
            // above/below flip and the left/right shift (see the ResizeObserver
            // above), so the squared corner always follows the pin.
            {
              "rounded-tl-none": pinCorner === "tl",
              "rounded-tr-none": pinCorner === "tr",
              "rounded-bl-none": pinCorner === "bl",
              "rounded-br-none": pinCorner === "br",
            },
          )}
          ref={containerRef}
          data-testid="canvas-comment-box"
          data-pin-corner={pinCorner}
          data-auth-trigger="view-comments"
          // tldraw binds a non-passive wheel listener on its canvas container
          // (use-gesture) that pans/zooms on scroll. React delegates
          // `onWheelCapture` at the app root, so stopping propagation here —
          // before the event bubbles to tldraw's container listener — lets the
          // inner region scroll natively while the canvas stays put. We don't
          // preventDefault, so native scrolling of the body still works.
          onWheelCapture={stopEventPropagation}
        >
          <Flex className="shrink-0 items-center justify-end pr-2 pt-2">
            <IconButton
              data-testid="canvas-comment-close-button"
              aria-label="Close comment"
              size="1"
              variant="ghost"
              color="gray"
              onClick={() => {
                this.editor.selectNone();
                dispatch(setActiveCommentId(null));
              }}
              className="cursor-pointer"
              onPointerDown={stopEventPropagation}
            >
              <X size={16} />
            </IconButton>
          </Flex>

          {/* Bounded flex column that gives the inner regions a height to
              shrink against. It must NOT scroll itself (`overflow-hidden`):
              the comment body and the pinned reply footer are managed by the
              Comment component (and the compose/login-wall branches scroll
              themselves), so an outer scroll here would unpin the reply box. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {renderCommentContents()}
          </div>
        </div>
      </HTMLContainer>
    );
  };

  indicator = (_shape: CommentShape) => null;
}
